/**
 * File Upload Configuration Service
 * Fetches and caches file upload settings from the backend
 */

import api from './api/client';

export interface FileUploadConfig {
  max_file_size_bytes: number;
  max_file_size_mb: number;
  max_files_per_transaction: number;
  allowed_extensions: string[];
  file_uploads_enabled: boolean;
}

// Default configuration (fallback if API fails)
const DEFAULT_CONFIG: FileUploadConfig = {
  max_file_size_bytes: 10 * 1024 * 1024, // 10MB
  max_file_size_mb: 10,
  max_files_per_transaction: 20,
  allowed_extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'jpg', 'jpeg', 'png', 'gif'],
  file_uploads_enabled: true,
};

// Cache for the configuration
let cachedConfig: FileUploadConfig | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

class FileUploadConfigService {
  /**
   * Get file upload configuration from backend
   * Uses caching to avoid repeated API calls
   */
  async getConfig(): Promise<FileUploadConfig> {
    // Check cache validity
    const now = Date.now();
    if (cachedConfig && (now - cacheTimestamp) < CACHE_TTL) {
      return cachedConfig;
    }

    try {
      const response = await api.get('/system/file-upload-config/');
      if (response.data && response.data.success !== false) {
        cachedConfig = {
          max_file_size_bytes: response.data.max_file_size_bytes,
          max_file_size_mb: response.data.max_file_size_mb,
          max_files_per_transaction: response.data.max_files_per_transaction,
          allowed_extensions: response.data.allowed_extensions,
          file_uploads_enabled: response.data.file_uploads_enabled,
        };
        cacheTimestamp = now;
        return cachedConfig;
      }
    } catch (error) {
      console.warn('Failed to fetch file upload config, using defaults:', error);
    }

    // Return defaults on error
    return DEFAULT_CONFIG;
  }

  /**
   * Clear the cached configuration
   * Call this when settings are updated in admin panel
   */
  clearCache(): void {
    cachedConfig = null;
    cacheTimestamp = 0;
  }

  /**
   * Get maximum file size in bytes
   */
  async getMaxFileSize(): Promise<number> {
    const config = await this.getConfig();
    return config.max_file_size_bytes;
  }

  /**
   * Get maximum file size in MB
   */
  async getMaxFileSizeMB(): Promise<number> {
    const config = await this.getConfig();
    return config.max_file_size_mb;
  }

  /**
   * Get allowed file extensions
   */
  async getAllowedExtensions(): Promise<string[]> {
    const config = await this.getConfig();
    return config.allowed_extensions;
  }

  /**
   * Validate file against current configuration
   * Returns error message if invalid, null if valid
   */
  async validateFile(file: File): Promise<string | null> {
    const config = await this.getConfig();

    // Check if uploads are enabled
    if (!config.file_uploads_enabled) {
      return 'File uploads are currently disabled';
    }

    // Check file size
    if (file.size > config.max_file_size_bytes) {
      return `File size (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds maximum allowed size (${config.max_file_size_mb} MB)`;
    }

    // Check file extension
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!config.allowed_extensions.includes(extension)) {
      return `File type ".${extension}" is not allowed. Allowed types: ${config.allowed_extensions.join(', ')}`;
    }

    return null; // File is valid
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

const fileUploadConfigService = new FileUploadConfigService();
export default fileUploadConfigService;
